import { AdsAssistantIndexToolbar } from "@/components/ads-assistant/AdsAssistantIndexToolbar";
import {
  AdsAssistantAggregationPostsList,
  type AdsGenerateOpen,
} from "@/components/ads-assistant/AdsAssistantAggregationPostsList";
import { AdsAssistantIndexPagination } from "@/components/ads-assistant/AdsAssistantIndexPagination";
import { ADS_INDEXED_ANCHOR_ID } from "@/components/ads-assistant/adsActivityCardIds";
import type { useAdsAssistantAggregation } from "@/components/ads-assistant/useAdsAssistantAggregation";
import { AdsAssistantBatchTemplateizeDialog } from "@/components/ads-assistant/AdsAssistantBatchTemplateizeDialog";
import { useAdsBatchTemplateize } from "@/components/ads-assistant/useAdsBatchTemplateize";

type Vm = ReturnType<typeof useAdsAssistantAggregation>;

export function AdsAssistantAggregationIndexedRegion(props: {
  vm: Vm;
  scopedOrgId: string;
  indexedPostsOpen: boolean;
  setIndexedPostsOpen: (open: boolean) => void;
  onCelebration: (msg: string) => void;
  onReassignAttribution: (post: import("@/lib/adsAssistantClient").AdsAssistantSocialPostRow | null) => void;
  onReload: () => void;
  onRequestGenerate: (p: AdsGenerateOpen) => void;
}) {
  const { vm } = props;
  const batchTemplateize = useAdsBatchTemplateize({ onApplied: props.onReload });

  if (vm.error) return null;

  return (
    <details
      id={ADS_INDEXED_ANCHOR_ID}
      className="rounded-lg border border-gray-200 bg-gray-50/60 open:bg-white dark:border-gray-600 dark:bg-zinc-900/30 dark:open:bg-zinc-900/50"
      open={props.indexedPostsOpen}
      onToggle={(e) => props.setIndexedPostsOpen(e.currentTarget.open)}
    >
      <summary className="cursor-pointer list-none px-2.5 py-2 text-xs font-medium text-gray-800 marker:hidden dark:text-gray-100 [&::-webkit-details-marker]:hidden">
        <span className="inline-flex flex-wrap items-center gap-2">
          <span aria-hidden className="text-gray-500 dark:text-gray-400">
            ▾
          </span>
          <span>Search previous posts</span>
          {!props.indexedPostsOpen && vm.total !== null ? (
            <span className="font-normal text-gray-500 dark:text-gray-400">({vm.total})</span>
          ) : null}
          {props.indexedPostsOpen && vm.loading && vm.total === null ? (
            <span className="font-normal text-gray-500 dark:text-gray-400">Loading…</span>
          ) : null}
        </span>
      </summary>
      <div className="space-y-3 border-t border-gray-200 px-3 pb-3 pt-2 dark:border-gray-600">
        {vm.canQuality ? (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-violet-200/80 bg-violet-50/50 px-2 py-1.5 text-[11px] text-violet-950 dark:border-violet-900/50 dark:bg-violet-950/25 dark:text-violet-100/90">
            <span>
              Trainer tool — batch-suggest templates for posts that have none. You review every proposal before it&apos;s saved.
            </span>
            <button
              type="button"
              className="rounded border border-violet-300 bg-white px-2 py-1 text-xs font-medium text-violet-950 hover:bg-violet-50 disabled:opacity-50 dark:border-violet-800 dark:bg-zinc-900 dark:text-violet-100 dark:hover:bg-zinc-800"
              onClick={() => void batchTemplateize.openDialog()}
            >
              Batch templateize…
            </button>
          </div>
        ) : null}
        <AdsAssistantIndexToolbar
          platformOptions={vm.platformOptions}
          orgLocations={vm.orgLocations}
          orgLocationsLoaded={vm.orgLocationsLoaded}
          indexScope={vm.indexScope}
          setIndexScope={vm.setIndexScope}
          indexFilterPlatform={vm.indexFilterPlatform}
          setIndexFilterPlatform={vm.setIndexFilterPlatform}
          indexLocationId={vm.indexLocationId}
          setIndexLocationId={vm.setIndexLocationId}
          indexSort={vm.indexSort}
          setIndexSort={vm.setIndexSort}
          indexSearchDraft={vm.indexSearchDraft}
          setIndexSearchDraft={vm.setIndexSearchDraft}
          applyIndexSearch={vm.applyIndexSearch}
          indexTagId={vm.indexTagId}
          setIndexTagId={vm.setIndexTagId}
          postTagDefs={vm.postTagDefs}
          indexGreenOnly={vm.indexGreenOnly}
          setIndexGreenOnly={vm.setIndexGreenOnly}
          clearIndexFilters={vm.clearIndexFilters}
          disabled={vm.loading}
        />
        {!vm.loading && vm.total === 0 ? (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {vm.hasActiveIndexFilters
              ? "No posts match these filters."
              : vm.showManualForm
                ? "No indexed posts yet. Add one with the form above."
                : "No indexed posts yet. Use Ingestion to add posts (trainers: Feed import tab)."}
          </p>
        ) : null}
        {vm.total !== null && vm.total > 0 ? (
          <AdsAssistantIndexPagination
            total={vm.total}
            page={vm.indexPage}
            pageSize={vm.indexPageSize}
            onPageChange={vm.setIndexPage}
            onPageSizeChange={vm.setIndexPageSize}
            disabled={vm.loading}
          />
        ) : null}
        <AdsAssistantAggregationPostsList
          posts={vm.posts}
          templates={vm.templates}
          locationNameById={Object.fromEntries(vm.orgLocations.map((l) => [l.id, l.name]))}
          canQuality={vm.canQuality}
          canIngestPosts={vm.canIngestPosts}
          canReassignAttribution={vm.canReassignAttribution}
          scopedOrgId={props.scopedOrgId}
          onCelebration={props.onCelebration}
          onReassignAttribution={props.onReassignAttribution}
          onReload={props.onReload}
          onRequestGenerate={props.onRequestGenerate}
        />
      </div>
      <AdsAssistantBatchTemplateizeDialog vm={batchTemplateize} />
    </details>
  );
}
